import { useMemo } from 'react';

import { useAuth } from '@arrived/auth';

import { OfferingFilterOption } from '../types';
import { assetTypeFilterOptions } from './assetTypes';
import { availabilityFilterOptions } from './availability';
import { leaseStatusFilterOptions } from './leaseStatus';
import { leverageFilterOptions } from './leverage';
import { ownershipFilterOptions } from './ownership';
import { tagIdFilterOptions } from './tagId';

const checkForNeedsAuth = (filters: Readonly<OfferingFilterOption[]>, isAuthenticated: boolean) =>
  filters.map((f) => {
    if (f.needsAuth) {
      return { ...f, disabled: !isAuthenticated };
    }

    return f;
  });

const FilterOptions = {
  availability: availabilityFilterOptions,
  leverage: leverageFilterOptions,
  leaseStatus: leaseStatusFilterOptions,
  tagId: tagIdFilterOptions,
  assetType: assetTypeFilterOptions,
  ownership: ownershipFilterOptions,
} as const;

/**
 * Get the filter options that can be used within the application.
 * @returns {FilterOptions} - Object with keys of filter types and values of filter options
 */
export const useFilterOptions = () => {
  const { isAuthenticated } = useAuth();

  return useMemo(
    () =>
      Object.entries(FilterOptions).reduce(
        (filterObj, [filterKey, filterList]) => ({
          ...filterObj,
          [filterKey]: checkForNeedsAuth(filterList, isAuthenticated),
        }),
        {} as typeof FilterOptions
      ),
    [isAuthenticated]
  );
};
